import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Strategy, StrategyListResponse } from '../types/client';
import { strategiesApi } from '@/services/api';
import { formatDate } from '@/utils/formatters';
import DataTable, { ColumnDef } from '@/components/utils/DataTable';
import { useTableState } from '@/hooks/useTableState';
import { useScope } from '@/hooks/useScope';
import { WRITE_GENERAL_MASTERS } from '@/constants/scopeCodes';
import { ActiveStatus, Button, Icon, RowActionMenu } from '@/components/ui';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';

interface StrategyListProps {
  onStrategySelect?: (strategy: Strategy) => void;
}

const StrategyList: React.FC<StrategyListProps> = ({ onStrategySelect }) => {
  const navigate = useNavigate();
  const canWrite = useScope(WRITE_GENERAL_MASTERS);
  const [strategies, setStrategies] = useState<Strategy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const {
    currentPage, pageSize, ordering, searchQuery, debouncedSearch,
    setTotalCount, onSort, onSearchChange, onPageChange,
    totalCount, totalPages, sortField, sortDir,
  } = useTableState({ defaultSortField: 'name', searchDebounceMs: 400 });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, unknown> = {
        page: currentPage,
        page_size: pageSize,
        ordering,
      };
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();

      const response: StrategyListResponse = await strategiesApi.getStrategies(params);
      setStrategies(response.results);
      setTotalCount(response.count);
    } catch (err) {
      console.error('Error fetching strategies:', err);
      setError('Failed to fetch strategies');
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, ordering, debouncedSearch, setTotalCount]);

  useEffect(() => {
    load();
  }, [load]);

  const columns = useMemo((): ColumnDef<Strategy>[] => [
    { key: 'code', header: 'Code', sortable: true, render: r => r.code },
    { key: 'name', header: 'Name', sortable: true, render: r => r.name },
    { key: 'firm_type', header: 'Firm Type', sortable: true, render: r => r.firm_type_display || 'N/A' },
    { key: 'created_ts', header: 'Created', sortable: true, render: r => formatDate(r.created_ts) },
    {
      key: 'is_active', header: 'Status', render: r => <ActiveStatus active={!!r.is_active} />,
    },
    {
      key: 'actions', header: 'Actions', width: '90px', render: r => (
        <RowActionMenu
          label={`Actions for ${r.code || r.name}`}
          actions={[
            { label: 'View Details', icon: <VisibilityIcon fontSize="small" />, onClick: () => (onStrategySelect ? onStrategySelect(r) : navigate(`/strategies/${r.id}`)) },
            { label: 'Edit', icon: <EditIcon fontSize="small" />, hidden: !canWrite, onClick: () => navigate(`/strategies/${r.id}/edit`) },
          ]}
        />
      ),
    },
  ], [navigate, onStrategySelect, canWrite]);

  const headerActions = canWrite ? (
    <Button variant="primary" icon={<Icon name="plus" size={14} />} onClick={() => navigate('/strategies/new')}>
      New Strategy
    </Button>
  ) : undefined;

  return (
    <DataTable
      data={strategies}
      columns={columns}
      rowKey={r => r.id}
      totalCount={totalCount}
      loading={loading}
      error={error}
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={onPageChange}
      pageSize={pageSize}
      sortField={sortField}
      sortDir={sortDir}
      onSort={onSort}
      searchQuery={searchQuery}
      onSearchChange={onSearchChange}
      searchPlaceholder="Search by strategy name or code..."
      title="Investment Strategies"
      headerActions={headerActions}
      rowStyle={r => (!r.is_active ? { opacity: 0.7, backgroundColor: 'var(--surface-2)' } : undefined)}
      emptyMessage="No strategies found."
    />
  );
};

export default StrategyList;